// Shared copy formatters — one phrasing per fact, app-wide.

/**
 * Reliability line: "12 partidos · sin ausencias" / "34 partidos · 1 ausencia".
 * Single source so the trust signal reads identically on every surface
 * (match detail, public preview, player profile).
 */
export function formatReliability(matches: number, noShows: number): string {
  const absences =
    noShows === 0 ? 'sin ausencias' : noShows === 1 ? '1 ausencia' : `${noShows} ausencias`
  return `${matches} partidos · ${absences}`
}

/**
 * The VOUCHING line: what happened to the people this player brought.
 *
 * Separate from `formatReliability` because they answer different questions —
 * "does this person turn up" and "are the people they bring any good". They
 * shared one counter until 2026-08-09, which made both unreadable: "2
 * ausencias" could not tell you which of the two it meant.
 *
 * Returns null for a player who has never vouched for anyone, which is most
 * players. A zero here would be noise on every profile in the app to carry a
 * fact about almost none of them.
 *
 * `person` switches voice for your OWN profile without forking the logic — the
 * first version hand-wrote the second-person sentence at the call site, which
 * is two phrasings of one fact and exactly what this module exists to prevent.
 */
export function formatVouching(
  guestShows: number,
  guestNoShows: number,
  person: 'third' | 'second' = 'third',
): string | null {
  const total = guestShows + guestNoShows
  if (total <= 0) return null
  const players = total === 1 ? '1 jugador' : `${total} jugadores`
  const brought = person === 'second' ? `Has traído a ${players}` : `Ha traído a ${players}`
  if (guestNoShows === 0) return `${brought} · ${total === 1 ? 'apareció' : 'aparecieron todos'}`
  if (guestNoShows === total) return `${brought} · ${total === 1 ? 'no apareció' : 'ninguno apareció'}`
  return `${brought} · ${guestNoShows} no ${guestNoShows === 1 ? 'apareció' : 'aparecieron'}`
}

/**
 * How much a level can be trusted, in words.
 *
 * `levelReliability` (0–100) and `levelSource` were written by the calibration
 * CF and carried on the public projection, but appeared in ZERO components — so
 * a 3.7 confirmed across a dozen competitive matches looked identical to a 3.5
 * somebody guessed at signup. That gap is the whole reason a casual player
 * hesitates to join strangers: not knowing whether the numbers mean anything.
 *
 * Deliberately qualitative. The raw 0–100 is an implementation detail of the
 * ELO-lite K-factor (starts 10, +15 per competitive match, caps 100 — so ~6
 * matches saturates it); showing "reliability 85" would invite players to read
 * precision that isn't there. Words carry the same signal honestly.
 *
 * Returns null when there is no level to qualify — callers render nothing.
 */
export function formatLevelConfidence(
  levelSource: string | null | undefined,
  levelReliability: number | null | undefined,
): string | null {
  switch (levelSource) {
    case 'computed': {
      const r = levelReliability ?? 0
      // Below ~3 competitive matches the number is still moving enough that
      // calling it "confirmado" would oversell it.
      if (r >= 70) return 'Confirmado en pista'
      if (r >= 40) return 'Ajustándose con cada partido'
      return 'Recién calibrado'
    }
    case 'self':
      return 'Autoevaluado'
    case 'club':
      return 'Estimado por su club'
    case 'playtomic':
      return 'Importado de Playtomic'
    default:
      return null
  }
}
